<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, watch, withDefaults, type Ref } from 'vue';
import BaseSlide from '@/components/slides/BaseSlide.vue';
import GifControls from '@/components/slides/GifControls.vue';
import VideoControls from '@/components/slides/VideoControls.vue';
import type { MediaSlideEmits as Emits, MediaSlideProps as Props } from '@/components/slides/slideTypes';
import { useTrackedVideo } from '@/libraries/vue-fetch/composables/useTrackedVideo';
import { useImageProgress } from '@/libraries/vue-fetch/composables/useImageProgress';
import { useApiStore } from '@/stores/api';
import { useMediaHotkeys, type GifHandle } from '@/composables/useMediaHotkeys';
import { buildUrlWithParams } from '@/requests/url';
import { ReactiveImage } from '@/libraries/vue-ractive-new-html-image/ref-img';
import LoadingPlaceholder from '@/components/loading/LoadingPlaceholder.vue';
import SkipButton from '@/components/loading/SkipButton.vue';

const props = withDefaults(
  defineProps<Props>(),
  {
    media: undefined,
    isVisible: true,
    isCurrent: false,
    canRevealResolvedMedia: true,
    showBlockingLoader: false,
    canSkipLoader: false,
  },
);
const emit = defineEmits<Emits>();
const store = useApiStore();
const FALLBACK_RATIO = 1;

const isImage = computed<boolean>(() =>
  props.media ? props.media.mime.startsWith('image/') : true,
);
const isVideo = computed<boolean>(() =>
  props.media ? props.media.mime.startsWith('video/') : false,
);
const isGif = computed<boolean>(() =>
  props.media ? props.media.mime === 'image/gif' : false,
);
const ratio = computed<number | undefined>(() => {
  if (!props.media) return undefined;
  const w = props.media.width ?? props.media.thumbnail_width;
  const h = props.media.height ?? props.media.thumbnail_height;
  if (!w || !h) return undefined;
  return w / h;
});
const effectiveRatio = computed<number>(() => ratio.value ?? FALLBACK_RATIO);
const hasMetadata = computed(() => props.media !== undefined);

const baseSlideElement = ref<InstanceType<typeof BaseSlide> | undefined>(undefined);

const shouldLoad = ref<boolean>(false);
const mainImageLoaded = ref<boolean>(false);

const videoRef = ref<null | HTMLVideoElement>(null);
const gifControlsRef = ref<InstanceType<typeof GifControls> | null>(null);

watch(
  [() => props.isVisible, () => props.isCurrent],
  ([isVisible, isCurrent]) => {
    const shouldBeLoaded = Boolean(isVisible) || Boolean(isCurrent);
    shouldLoad.value = shouldLoad.value || shouldBeLoaded;

    if (isVideo.value && videoRef.value) {
      if (isCurrent) {
        videoRef.value.currentTime = 0;
        videoRef.value.play();
      } else {
        videoRef.value.pause();
      }
    }
  },
  { immediate: true },
);

// Reset loaded state when source changes
watch(() => props.source.id, () => { mainImageLoaded.value = false; });

const SLIDE_COLORS = ['#0f0c10', '#12101a', '#0d1017', '#100d14', '#0c0f16'];
const slideColor = SLIDE_COLORS[props.source.id % SLIDE_COLORS.length];

// Load as soon as the slide becomes visible — don't gate on metadata/ratio (causes stutter)
const srcIfVisible = computed(() => shouldLoad.value ? props.source.src : '');

// Track download progress when the image starts loading
const {
  blobUrl: imageBlobUrl,
  isLoading: imageIsLoading,
  progress: imageProgress,
  loadedBytes: imageLoadedBytes,
  totalBytes: imageTotalBytes,
  bytesPerSecond: imageBytesPerSecond,
  etaSeconds: imageEtaSeconds,
} = useImageProgress(
  computed(() => props.source.src),
  // only track when we should load AND it's an image AND we have metadata
  computed(() => shouldLoad.value && isImage.value && !!props.media),
);

// Only render the main media once metadata has arrived so the placeholder/loading state stays coherent.
const effectiveSrc = computed(() =>
  hasMetadata.value ? (imageBlobUrl.value || srcIfVisible.value) : '',
);

// Thumbnail only requires sessionKey; no need to wait for full metadata
const thumbnailSrc = computed(() => {
  if (!store.sessionKey) return '';
  return buildUrlWithParams(store.host + '/get_files/thumbnail', {
    file_id: props.source.id.toString(),
    'Hydrus-Client-API-Session-Key': store.sessionKey,
  }).toString();
});

// Placeholder image: shows the blurhash data URL immediately, then the real
// thumbnail once it has finished loading. Both are sourced through a single
// ReactiveImage so displaySrc automatically picks whichever is ready first.
const placeholderImage = ref<ReactiveImage | null>(null);
watch(
  [
    () => props.source.id,
    () => props.media?.blurhash,
    () => props.media?.thumbnail_width,
    () => props.media?.thumbnail_height,
    thumbnailSrc,
  ] as const,
  ([
    ,
    blurhash,
    thumbW,
    thumbH,
    thumbSrc,
  ]) => {
    placeholderImage.value = new ReactiveImage(
      thumbW ?? undefined,
      thumbH ?? undefined,
      blurhash ?? undefined,
      thumbSrc || undefined,
    );
  },
  { immediate: true },
);
const showLoaderState = computed(() =>
  props.showBlockingLoader && isImage.value && props.media && !mainImageLoaded.value,
);
const showMetadataLoading = computed(() =>
  props.showBlockingLoader && !props.media,
);
const showDeferredPlaceholder = computed(() =>
  !props.media && !props.showBlockingLoader,
);
const showResolvedMedia = computed(() =>
  props.canRevealResolvedMedia &&
  (props.isCurrent || props.isVisible || props.showBlockingLoader),
);
const presentationReady = computed(() => {
  if (!props.media) return false;
  if (!props.canRevealResolvedMedia) return false;
  if (isImage.value) return mainImageLoaded.value;
  return true;
});

watch(
  presentationReady,
  (ready) => {
    emit('presentationReadyChange', ready);
  },
  { immediate: true },
);

function onMainImageLoad(event: Event) {
  // Guard: ignore load events from the placeholder src; only mark as loaded
  // once the real effectiveSrc has rendered.
  if (!effectiveSrc.value) return;
  mainImageLoaded.value = true;
  if (!props.media) return;
  const img = event.target as HTMLImageElement;
  const { naturalWidth: nw, naturalHeight: nh } = img;
  const mw = props.media.width;
  const mh = props.media.height;
  if (mw !== undefined && mh !== undefined && (nw !== mw || nh !== mh)) {
    console.warn(
      `[MediaSlide] Size mismatch for id:${props.source.id} — ` +
      `metadata says ${mw}×${mh} but image is ${nw}×${nh}`,
    );
  }
}

useTrackedVideo(videoRef, srcIfVisible);
useMediaHotkeys(
  videoRef,
  gifControlsRef as unknown as Ref<GifHandle | null>,
  computed(() => props.isCurrent ?? false),
  isVideo,
  isGif,
);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
</script>

<template>
  <BaseSlide
    :is-mobile="props.isMobile"
    :style="{ aspectRatio: effectiveRatio, background: slideColor }"
    :class="{ 'is-current': props.isCurrent }"
    :data-media="JSON.stringify(media)"
    ref="baseSlideElement"
  >
    <!-- Download progress overlay: shown when metadata is available but image not yet loaded -->
    <div
      v-if="showLoaderState && isImage && props.media && imageIsLoading"
      class="download-progress"
    >
      <div
        class="progress-bar-track"
      >
        <div
          class="progress-bar-fill"
          :style="{ width: imageTotalBytes > 0 ? `${imageProgress}%` : '100%' }"
          :class="{ indeterminate: imageTotalBytes === 0 }"
        />
      </div>
      <div
        class="progress-stats"
      >
        <span
          v-if="imageTotalBytes > 0"
        >
          {{ formatBytes(imageLoadedBytes) }} / {{ formatBytes(imageTotalBytes) }}
          ({{ Math.round(imageProgress) }}%)
        </span>
        <span
          v-else
        >
          {{ formatBytes(imageLoadedBytes) }}
        </span>
        <span
          v-if="imageBytesPerSecond > 0"
        >
          · {{ formatBytes(imageBytesPerSecond) }}/s
        </span>
        <span
          v-if="imageEtaSeconds !== null && imageEtaSeconds > 1"
        >
          · ~{{ Math.ceil(imageEtaSeconds) }}s
        </span>
      </div>
      <SkipButton
        @skip="emit('skipLoader')"
        :can-skip-loader="props.canSkipLoader"
      />
    </div>
    <LoadingPlaceholder
      v-if="showMetadataLoading"
      class="slide-loader"
      @skip="emit('skipLoader')"
      :props="props"
      :medium="'metadata'"
    />
    <div
      v-else-if="showDeferredPlaceholder"
      class="deferred-placeholder"
    >
      <span
        class="deferred-placeholder-text"
      >
        Pending…
      </span>
    </div>

    <!-- Main image (non-GIF): starts with the blurhash/thumbnail placeholder src
         and switches to effectiveSrc once the real image is downloaded.
         The thumbnail-placeholder class applies the blur/scale while loading. -->
    <img
      v-if="props.source && isImage && props.media && !isGif"
      v-show="showResolvedMedia"
      alt="gallery image"
      :src="effectiveSrc || placeholderImage?.displaySrc.value || ''"
      :class="{ 'thumbnail-placeholder': !mainImageLoaded, 'main-loaded': mainImageLoaded }"
      class="main-media media-layer"
      @load="onMainImageLoad"
      @click="emit('mediaClicked', 'img', $event)"
    >
    <!-- GIF player with frame controls -->
    <GifControls
      v-if="props.source && isGif && props.media"
      v-show="showResolvedMedia"
      ref="gifControlsRef"
      :src="effectiveSrc"
      :class="{ 'main-loaded': mainImageLoaded }"
      class="main-media media-layer"
      @loaded="mainImageLoaded = true"
      @click="emit('mediaClicked', 'img', $event)"
    />
    <video
      ref="videoRef"
      loop
      v-else-if="props.source && isVideo && props.media"
      v-show="showResolvedMedia"
      autoplay
      @click="emit('mediaClicked', 'video', $event)"
      :muted="!isVisible"
      playsinline
      class="main-media media-layer"
    >
      <source
        :type="props.media?.mime || 'unknown/unknown'"
        :src="srcIfVisible"
      >
      gallery video
    </video>
    <VideoControls
      v-if="props.source && isVideo && props.media"
      v-show="showResolvedMedia"
      :video="videoRef"
      :src="srcIfVisible"
      class="video-controls-layer media-layer"
    />
    <LoadingPlaceholder
      v-else-if="props.showBlockingLoader"
      class="slide-loader"
      @skip="emit('skipLoader')"
      :props="props"
      :medium="'media'"
    />
  </BaseSlide>
</template>

<style lang="scss" scoped>
.visible {
  opacity: 0.8;
}

:deep(.base-slide) {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
}

.is-current {
  img, video, span {
    outline: 5px dotted var(--accent);
    outline-offset: -5px;
  }
}
.base-slide.slide-mobile {
  &,
  img,
  video {
    height: auto;
    width: 100%;
  }
}
.base-slide.slide-desktop {
  img,
  video {
    height: 100%;
    width: auto;
  }
}

.media-layer,
.slide-loader,
.deferred-placeholder,
.download-progress {
  grid-area: 1 / 1;
}

.video-controls-layer {
  align-self: end;
  z-index: 1;
}

.media-layer,
.slide-loader,
.deferred-placeholder {
  width: 100%;
  height: 100%;
}

.slide-loader,
.deferred-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-media {
  transition: opacity 0.2s ease;

  &.thumbnail-placeholder {
    // Force fill regardless of desktop-mode width: auto override.
    width: 100% !important;
    height: 100% !important;
    overflow: hidden;
    object-fit: cover;
    pointer-events: none;
  }
}

.download-progress {
  align-self: end;
  justify-self: center;
  width: min(320px, 90%);
  margin-bottom: 8px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar-track {
  height: 4px;
  background: rgba(255,255,255,0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--accent, #7f7);
  border-radius: 2px;
  transition: width 0.2s ease;

  &.indeterminate {
    animation: indeterminate-slide 1.2s infinite ease-in-out;
    width: 40% !important;
  }
}

@keyframes indeterminate-slide {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.progress-stats {
  font-size: 0.7rem;
  color: rgba(255,255,255,0.85);
  text-align: center;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  white-space: nowrap;
}

.deferred-placeholder {
  z-index: 1;
  color: rgba(255,255,255,0.6);
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}

.deferred-placeholder-text {
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

@keyframes slide-spin {
  to { transform: rotate(360deg); }
}

img {
  /* noinspection CssInvalidPropertyValue,CssOverwrittenProperties */
  &.crisp-gif {
    /*
     * Source - https://stackoverflow.com/a/14068216
     * Posted by Vargr, modified by community.
     * Retrieved 2026-04-04, License - CC BY-SA 4.0
    */
    image-rendering: optimizeSpeed;             /* STOP SMOOTHING, GIVE ME SPEED  */
    image-rendering: -moz-crisp-edges;          /* Firefox                        */
    image-rendering: -o-crisp-edges;            /* Opera                          */
    image-rendering: -webkit-optimize-contrast; /* Chrome (and eventually Safari) */
    image-rendering: optimize-contrast;         /* CSS3 Proposed                  */
    -ms-interpolation-mode: nearest-neighbor;   /* IE8+                           */
    image-rendering: pixelated;                 /* Universal support since 2021   */
  }
}
</style>
